import * as React from 'react';
import {Mutation} from 'react-apollo';

import type {
    CreateEmailSequenceTemplate as CreateEmailSequenceTemplateMutation,
    CreateEmailSequenceTemplateVariables as CreateEmailSequenceTemplateMutationVariables,
    UpdateEmailSequenceTemplate as UpdateEmailSequenceTemplateMutation,
    UpdateEmailSequenceTemplateVariables as UpdateEmailSequenceTemplateMutationVariables,
    UndeleteEmailSequenceTemplate as UndeleteEmailSequenceTemplateMutation,
    UndeleteEmailSequenceTemplateVariables as UndeleteEmailSequenceTemplateMutationVariables,
    EmailSequenceTemplateCreateInput,
    EmailSequenceTemplateUpdateInput,
    GetUserEmailSequenceTemplates as GetUserEmailSequenceTemplatesQuery,
} from 'nutshell-graphql-types';

import {bustManagedCollectionCache} from '../../../util/bust-managed-collection-cache';

import CREATE_EMAIL_SEQUENCE_TEMPLATE from '../graphql/mutations/create-email-sequence-template.graphql';
import UPDELETE_EMAIL_SEQUENCE_TEMPLATE from '../graphql/mutations/undelete-email-sequence-template.graphql';
import UPDATE_EMAIL_SEQUENCE_TEMPLATE from '../graphql/mutations/update-email-sequence-template.graphql';
import GET_USER_EMAIL_SEQUENCE_TEMPLATES from '../graphql/queries/get-user-email-sequence-templates.graphql';

import {EmailTemplateDesignerFormComponent} from './email-template-designer-form-component';
import type {EmailSequenceTemplate} from './email-template-designer-modal';

type OwnProps = {
    closeModal: () => void;
    isLoading: boolean;
    emailSequenceTemplate: EmailSequenceTemplate | null | undefined;
    onSetShowProInfo: (value: boolean) => void;
    startEditingSharedTemplate: () => void;
    onChildTemplateSaved: () => void;
    onUserChildTemplateDirtied: () => void;
    shouldShowProInfo: boolean;
    onRefetch?: () => Promise<any>;
    groupId?: string | null | undefined;
    refetchGroups?: () => Promise<any>;
    createEmailSequenceTemplate?: (
        sequenceTemplate: EmailSequenceTemplateCreateInput
    ) => Promise<any>;
};

class CreateMutation extends Mutation<
    CreateEmailSequenceTemplateMutation,
    CreateEmailSequenceTemplateMutationVariables
> {}
class UpdateMutation extends Mutation<
    UpdateEmailSequenceTemplateMutation,
    UpdateEmailSequenceTemplateMutationVariables
> {}
class UndeleteSequenceMutation extends Mutation<
    UndeleteEmailSequenceTemplateMutation,
    UndeleteEmailSequenceTemplateMutationVariables
> {}

export const EmailTemplateDesignerForm = (props: OwnProps) => (
    <CreateMutation mutation={CREATE_EMAIL_SEQUENCE_TEMPLATE}>
        {(createTemplate) => (
            <UpdateMutation mutation={UPDATE_EMAIL_SEQUENCE_TEMPLATE}>
                {(updateTemplate) => (
                    <UndeleteSequenceMutation mutation={UPDELETE_EMAIL_SEQUENCE_TEMPLATE}>
                        {(undeleteSequenceTemplate) => {
                            const {createEmailSequenceTemplate, onRefetch, ...restProps} = props;

                            return (
                                <EmailTemplateDesignerFormComponent
                                    {...restProps}
                                    createSequenceTemplate={(sequenceTemplate) => {
                                        const input: EmailSequenceTemplateCreateInput = {
                                            emailSequenceTemplate: {
                                                name: sequenceTemplate.name,
                                                isShared: sequenceTemplate.isShared,
                                                groupId: sequenceTemplate.groupId,
                                                parentEmailSequenceTemplateId:
                                                    sequenceTemplate.parentEmailSequenceTemplateId,
                                                emailSequenceTemplateMessages:
                                                    sequenceTemplate.emailSequenceTemplateMessages.map(
                                                        (template) => {
                                                            return {
                                                                subject: template.subject,
                                                                body: template.bodyHTML,
                                                                useSignature: template.useSignature,
                                                                sendDelay: template.delay,
                                                                sendAsReply: template.sendAsReply,
                                                            };
                                                        }
                                                    ),
                                            },
                                        };

                                        if (createEmailSequenceTemplate) {
                                            return createEmailSequenceTemplate(input);
                                        } else {
                                            return createTemplate({
                                                variables: {
                                                    emailSequenceTemplate: input,
                                                },
                                                update: (proxy, mutationResult) => {
                                                    const newSequenceTemplate =
                                                        mutationResult &&
                                                        mutationResult.data &&
                                                        mutationResult.data
                                                            .emailSequenceTemplateCreate
                                                            .emailSequenceTemplate;
                                                    if (!newSequenceTemplate) return;

                                                    const cacheResult:
                                                        | GetUserEmailSequenceTemplatesQuery
                                                        | null
                                                        | undefined = proxy.readQuery({
                                                        query: GET_USER_EMAIL_SEQUENCE_TEMPLATES,
                                                    });

                                                    if (
                                                        !cacheResult ||
                                                        !cacheResult.emailSequenceTemplateCollection
                                                    )
                                                        return;

                                                    const oldTemplateCollection =
                                                        cacheResult.emailSequenceTemplateCollection;

                                                    proxy.writeQuery({
                                                        query: GET_USER_EMAIL_SEQUENCE_TEMPLATES,
                                                        data: {
                                                            emailSequenceTemplateCollection: {
                                                                ...oldTemplateCollection,
                                                                userTemplates:
                                                                    oldTemplateCollection.userTemplates.concat(
                                                                        // @ts-expect-error
                                                                        newSequenceTemplate
                                                                    ),
                                                            },
                                                        },
                                                    });
                                                },
                                            }).then(() => {
                                                bustManagedCollectionCache(
                                                    'emailSequenceTemplates'
                                                );
                                            });
                                        }
                                    }}
                                    updateSequenceTemplate={(sequenceTemplate) => {
                                        const input: EmailSequenceTemplateUpdateInput = {
                                            emailSequenceTemplate: {
                                                id: sequenceTemplate.id,
                                                name: sequenceTemplate.name,
                                                isShared: sequenceTemplate.isShared,
                                                groupId: sequenceTemplate.groupId,
                                                emailSequenceTemplateMessages:
                                                    sequenceTemplate.emailSequenceTemplateMessages.map(
                                                        (template) => {
                                                            return {
                                                                id: template.id.startsWith('new')
                                                                    ? undefined
                                                                    : template.id,
                                                                subject: template.subject,
                                                                body: template.bodyHTML,
                                                                useSignature: template.useSignature,
                                                                sendDelay: template.delay,
                                                                sendAsReply: template.sendAsReply,
                                                            };
                                                        }
                                                    ),
                                            },
                                        };

                                        return updateTemplate({
                                            variables: {
                                                emailSequenceTemplate: input,
                                            },
                                        });
                                    }}
                                    undeleteSequenceTemplate={(sequenceTemplateId) =>
                                        undeleteSequenceTemplate({
                                            variables: {
                                                sequenceTemplate: {
                                                    id: sequenceTemplateId,
                                                },
                                            },
                                        }).then(() => {
                                            if (onRefetch) onRefetch();
                                        })
                                    }
                                />
                            );
                        }}
                    </UndeleteSequenceMutation>
                )}
            </UpdateMutation>
        )}
    </CreateMutation>
);
